/** GERP CLAIM NOTE(166) — 레거시 appr_edit_forms_craim_note_mstr */
export const CLAIM_NOTE_TYPE = 166;

export const isClaimNoteType = (apvType: number): boolean =>
  apvType === CLAIM_NOTE_TYPE;

export const CLAIM_NOTE_MISSING_MESSAGE =
  '데이터를 로드하지 않았습니다. 조회 조건 선택 후 확인 버튼을 누르세요.';

/** 레거시 pre_cost_yn — GERP 에서 상신할 수 없는 건(draft-source 422) */
export const CLAIM_NOTE_BLOCKED_MESSAGE =
  '현재 결재가 회수상태 입니다.\n회수 상태에서는 결재내역이 추가 불가능합니다.';

export const CLAIM_NOTE_RESET_MESSAGE =
  '결재는 한 유형으로만 가능합니다. 기존 내역은 초기화 진행됩니다.';

export const CLAIM_NOTE_POPUP_BLOCKED_MESSAGE =
  '팝업이 차단되었습니다. 팝업 차단을 해제해주세요.';

/** 서버로 가는 값이라 한국어 그대로 — 화면 표시만 번역 */
export const CLAIM_NOTE_HOWLISTS = ['수입', '수출'] as const;
export type ClaimNoteHowlist = (typeof CLAIM_NOTE_HOWLISTS)[number];

/** 클레임 유형(GERP 공통코드 X044) — draft-source 에는 dtlCdNm 을 보냄 */
export type ClaimNoteClaimType = { dtlCd: string; dtlCdNm: string };

/** GERP 미상신 결재 건 — 키는 GERP 응답 그대로 */
export type ClaimNoteCandidate = {
  sysId: string;
  aprvId: string;
  aprvTypcd: string;
  aprvRev?: string | null;
  title?: string | null;
  docTit?: string | null;
  claimNoBiz?: string | null;
  realClaimNo?: string | null;
};

type Value = string | number | null | undefined;

/**
 * 레거시 CLAIM_HEADER(OMS_CLAIM_HD) 1행 — GERP 응답은 custCdNm·qty·sumKrw 만 확정.
 * 나머지는 레거시 열 이름(camelCase)으로 오면 그대로 표시
 */
export type ClaimNoteHeader = {
  custCdNm?: Value;
  bpCd?: Value;
  bpSendDateCh?: Value;
  brandNm?: Value;
  customer?: Value;
  creditNoBiz?: Value;
  style?: Value;
  colorCd?: Value;
  qty?: Value;
  sumKrw?: Value;
  currCd?: Value;
  /** 머리줄 괄호 안 클레임 유형명(X040) */
  craimType?: Value;
  /** 업체파일 — GERP 첨부 경로 */
  attachPath?: Value;
};

/** SCRAP 카드 합계 — 레거시 SCRAP_SUM·DEBIT_SUM 열 이름(camelCase) */
export type ClaimNoteScrapSummary = {
  scrapClaimQty?: Value;
  scrapClaimAmt?: Value;
  claimExchRate?: Value;
  debitQty?: Value;
  exchClaimAmt?: Value;
};

/** OZ 리포트 form POST 값 */
export type ClaimNoteOzPrint = {
  url?: string | null;
  gerpOzrNm?: string | null;
  gerpOdiNm?: string | null;
  pcount?: Value;
  args1?: string | null;
  locale?: string | null;
  claimKind?: string | null;
};

export type ClaimNoteCard = {
  aprvId: string;
  claimNoBiz?: string | null;
  realClaimNo?: string | null;
  gubun?: string | null;
  /** DEB 면 DEBIT 카드, 그 외 SCRAP 카드 */
  claimKind?: string | null;
  headers?: ClaimNoteHeader[] | null;
  scrapSummary?: ClaimNoteScrapSummary | ClaimNoteScrapSummary[] | null;
  ozPrint?: ClaimNoteOzPrint | null;
};

export type ClaimNoteDraftSource = {
  code: number;
  attributes: Record<string, unknown>;
  display: {
    claimTypes?: ClaimNoteClaimType[] | null;
    candidates: ClaimNoteCandidate[] | null;
    selection: string[] | null;
    subject: string | null;
    content: { cards?: ClaimNoteCard[] | null } | null;
  };
};

/** 작성 중 고른 건 1개 */
export type ClaimNoteItem = {
  aprvId: string;
  /** 레거시 data-credit_no — 건 제목(DOC_TIT) */
  label: string;
  /** 부분반려 창 제목의 차수 — 미상신 목록에만 있음 */
  aprvRev?: string;
  card: ClaimNoteCard;
};

/** 저장 attributes 와 같은 모양 */
export type ClaimNoteState = {
  systemId: string;
  approvalTypeCode: string;
  /** 고른 건, 쉼표로 이음 */
  approvalId: string;
  /** 클레임 유형 이름(dtlCdNm) */
  claimType: string;
  /** 작성 시점 카드 HTML */
  content: string;
};

/** 레거시 콤보 문구 — 건 제목(DOC_TIT) */
export const buildClaimNoteLabel = (c: ClaimNoteCandidate) =>
  c.docTit?.trim() || c.title?.trim() || c.aprvId;

/** 레거시 get_claim_note_html_mstr — CLAIM_NO 는 DOC_TIT 뒤 13자리 */
export const claimNoOf = (card: ClaimNoteCard, label: string) =>
  card.claimNoBiz?.trim() || label.slice(-13);

export const isDebitCard = (card: ClaimNoteCard, gubun?: string | null) =>
  card.claimKind ? card.claimKind === 'DEB' : (card.gubun ?? gubun) === 'DEBIT';

/** 레거시 change_title_content — 고른 순서의 첫 건 기준. "외" 앞 공백 없음도 레거시 그대로 */
export const buildClaimNoteSubject = (
  labels: string[],
  t: (key: string, options?: Record<string, unknown>) => string,
): string => {
  if (!labels.length) return '';
  const [first] = labels;
  if (labels.length === 1) return t('CLAIM Note ({{title}})', { title: first });
  return t('CLAIM Note ({{title}}외 {{count}}건)', {
    title: first,
    count: labels.length - 1,
  });
};

/** 레거시 본문 — 고를 때마다 덮어씀 */
export const buildClaimNoteEditorContent = (
  labels: string[],
  t: (key: string) => string,
): string =>
  `<p>&nbsp;</p><p>${t('추가')} : ${labels.join(
    ',',
  )}&nbsp;</p><p>&nbsp;</p><p>&nbsp;</p>`;

/** 레거시 howSelct 초기화 때 본문 */
export const CLAIM_NOTE_EMPTY_EDITOR_CONTENT =
  '<p>&nbsp;&nbsp;</p><p>&nbsp;</p><p>&nbsp;</p>';

/** 응답 카드 중 고른 건 — 없으면 첫 카드 */
export const pickClaimNoteCard = (
  source: ClaimNoteDraftSource,
  aprvId: string,
): ClaimNoteCard | null => {
  const cards = source.display.content?.cards ?? [];
  return cards.find((c) => c.aprvId === aprvId) ?? cards[0] ?? null;
};

const nonEmpty = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() ? v : null;

export const parseClaimNoteAttributes = (
  attributes: Record<string, unknown> | null | undefined,
): ClaimNoteState | null => {
  const content = nonEmpty(attributes?.content);
  if (!attributes || !content) return null;
  return {
    systemId: nonEmpty(attributes.systemId) ?? '',
    approvalTypeCode: nonEmpty(attributes.approvalTypeCode) ?? '',
    approvalId: nonEmpty(attributes.approvalId) ?? '',
    claimType: nonEmpty(attributes.claimType) ?? '',
    content,
  };
};

export const parseClaimNoteContent = (
  attributes: Record<string, unknown> | null | undefined,
): string | null => nonEmpty(attributes?.content);

export const buildClaimNoteAttributes = (
  state: ClaimNoteState | null | undefined,
) =>
  state
    ? {
        systemId: state.systemId,
        approvalTypeCode: state.approvalTypeCode,
        approvalId: state.approvalId,
        claimType: state.claimType,
        content: state.content,
      }
    : null;

export const checkClaimNoteBeforeSave = (
  state: ClaimNoteState | null | undefined,
): string | null =>
  state?.content && state.approvalId ? null : CLAIM_NOTE_MISSING_MESSAGE;

export const claimNoteKey = (state: ClaimNoteState | null | undefined) =>
  state?.approvalId ?? '';

export const CLAIM_NOTE_OZ_URL = 'https://report.fnf.co.kr/report/gerp_oz.jsp';

/** 레거시 문서 Print 버튼은 번호·DEB/SCR 만 들고 있어 나머지는 레거시 고정값 */
export const legacyClaimNoteOz = (
  realClaimNo: string,
  claimKind: string,
): ClaimNoteOzPrint => {
  const odi = claimKind === 'SCR' ? 'rtn_scrap_note' : 'rtn_claim_note';
  return {
    url: CLAIM_NOTE_OZ_URL,
    gerpOzrNm: `${odi}.ozr`,
    gerpOdiNm: odi,
    pcount: 1,
    args1: `claim_no=${realClaimNo}`,
    locale: 'en_US',
    claimKind,
  };
};

/**
 * 레거시 btn-printattachclaim — OZ 리포트를 새 창(SCMReport)에 form POST.
 * 레거시처럼 claim·scrap 두 서식 값을 함께 보냄(SCR 일 때만 scrap args1 채움).
 * 팝업이 막히면 false
 */
export const openClaimNoteOzReport = (oz: ClaimNoteOzPrint): boolean => {
  const url = oz.url?.trim() ?? '';
  // 저장 HTML 값이라 OZ 리포트 서버 주소만 허용
  if (
    !url.startsWith('https://report.fnf.co.kr/') ||
    !oz.gerpOdiNm ||
    !oz.args1
  )
    return true;
  const target = 'SCMReport';
  const win = window.open(
    '',
    target,
    'width=900,height=800,left=100,top=100,menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes',
  );
  if (!win || win.closed) return false;
  const pcount = String(oz.pcount ?? 1);
  const fields: Record<string, string> = {
    gerp_ozr_nm: oz.gerpOzrNm ?? '',
    gerp_odi_nm: oz.gerpOdiNm,
    'odi.rtn_claim_note.args1': oz.args1,
    'odi.rtn_scrap_note.args1': oz.claimKind === 'SCR' ? oz.args1 : '',
    'odi.rtn_claim_note.pcount': pcount,
    'odi.rtn_scrap_note.pcount': pcount,
    locale: oz.locale || 'en_US',
  };
  const form = document.createElement('form');
  form.method = 'post';
  form.action = url;
  form.target = target;
  Object.entries(fields).forEach(([name, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
  form.remove();
  return true;
};
